import { batch } from 'react-redux';

import { refreshFolders } from '.';
import { showShareTooltip } from './showShareTooltip';
import { notify } from '../../actions/notifications';
import {
  closeShareModal,
  setShareModalIsFetching,
  setShareModalUrl,
  setShareSettings,
} from '../../actions/shareCollection';
import { formatForWhatsApp } from '../../containers/ShareSettingsModal/SettingsModalContent/utils';
import {
  fetchWebShareFolder,
  IWebShareFolderRequest,
  IWebShareFolderResponse200,
  IWebShareFolderResponse400,
} from '../../repositories/shared-folders/v1/web-share-folder';
import { selectSelectedFolder } from '../../selectors/folders';
import { selectOfferShareSettings } from '../../selectors/shareCollection/selectOfferShareSettings';
import { ENotificationStyle } from '../../types/notifications';
import { TThunkAction } from '../../types/redux';
import { isCopyPromiseEnabled } from '../../utils/isCopyPromiseEnabled';

export enum EShareOptions {
  ForwardToTelegram = 'telegram',
  ForwardToWhatsApp = 'whatsapp',
  CopyLink = 'copy',
  Preview = 'preview',
  SaveSettings = 'save',
}

export const openInNewTab = (url: string) => {
  window.open(url, '_blank');
};

export const forwardToTelegram = (url: string) => {
  const text = '';
  const link = `https://t.me/share/url?url=${url}&text=${text}`;

  openInNewTab(link);
};
export const forwardToWhatsApp = (url: string) => {
  const link = `https://wa.me/?text=${formatForWhatsApp(formatForWhatsApp(url))}`;

  openInNewTab(link);
};

export const shareFolder =
  (options: EShareOptions, folderSettings?: Partial<IWebShareFolderRequest>): TThunkAction<Promise<void>> =>
  async (dispatch, getState, { httpApi, logger }) => {
    const showError = () => {
      dispatch(notify('Произошла ошибка при создании страницы подборки, попробуйте ещё раз', ENotificationStyle.Error));
    };

    const state = getState();

    const selectedCollection = selectOfferShareSettings(state);
    const selectedFolder = selectSelectedFolder(state);

    if (!selectedCollection) {
      logger.warning('Tried to share folder with no selected folder');

      return;
    }

    const { id, name, hideAddresses, hideContacts, hideNotes, hideOffersLink } = selectedCollection;
    const parameters: IWebShareFolderRequest = {
      folderId: id,
      name,
      hideAddress: hideAddresses,
      hideComments: hideNotes,
      hideContacts,
      hideOffersLink,
      ...folderSettings,
    };

    const folderName = selectedFolder?.name;

    const getLandingUrl = async (type: 'blob' | 'plain') => {
      const res = await fetchWebShareFolder<IWebShareFolderResponse200, IWebShareFolderResponse400>({
        httpApi,
        parameters,
      });

      if (res.statusCode === 200) {
        if (options === EShareOptions.SaveSettings) {
          dispatch(
            setShareSettings({
              id,
              name: parameters.name,
              hideNotes: parameters.hideComments || false,
              hideContacts: parameters.hideContacts || false,
              hideAddresses: parameters.hideAddress || false,
              hideOffersLink: Boolean(parameters.hideOffersLink),
              url: res.response.url,
            }),
          );
        } else {
          dispatch(setShareModalUrl({ id, value: res.response.url }));
        }

        return type === 'plain' ? res.response.url : new Blob([res.response.url], { type: 'text/plain' });
      }

      if (res.statusCode === 400) {
        showError();
        logger.warning(res.response.message);
      }

      return type === 'plain' ? '' : new Blob([''], { type: 'text/plain' });
    };

    dispatch(setShareModalIsFetching({ id, isFetchingType: options }));

    try {
      switch (options) {
        case EShareOptions.CopyLink: {
          if (isCopyPromiseEnabled()) {
            // Это будет работать только с 98 версии chrome
            // Сейчас он не поддерживает тип data-type Promise
            // https://bugs.chromium.org/p/chromium/issues/detail?id=1014310
            const type = 'text/plain';
            navigator.clipboard
              .write([new ClipboardItem({ [type]: getLandingUrl('blob') })])
              .then(() => {
                batch(() => {
                  dispatch(showShareTooltip('Ссылка скопирована'));
                  dispatch(setShareModalIsFetching({ id, isFetchingType: 'idle' }));
                });
              })
              .catch(e => {
                showError();
                logger.warning(e);
              });
          } else {
            const url = await getLandingUrl('plain');
            await navigator.clipboard.writeText(url as string);
            dispatch(showShareTooltip('Ссылка скопирована'));
            dispatch(setShareModalIsFetching({ id, isFetchingType: 'idle' }));
          }

          break;
        }

        case EShareOptions.ForwardToTelegram: {
          const url = await getLandingUrl('plain');

          forwardToTelegram(url as string);
          dispatch(setShareModalIsFetching({ id, isFetchingType: 'idle' }));
          break;
        }

        case EShareOptions.ForwardToWhatsApp: {
          const url = await getLandingUrl('plain');

          forwardToWhatsApp(url as string);
          dispatch(setShareModalIsFetching({ id, isFetchingType: 'idle' }));
          break;
        }

        case EShareOptions.Preview: {
          const url = await getLandingUrl('plain');

          openInNewTab(url as string);
          dispatch(setShareModalIsFetching({ id, isFetchingType: 'idle' }));
          break;
        }

        case EShareOptions.SaveSettings:
          await getLandingUrl('plain');

          batch(() => {
            if (folderName !== parameters.name.trim()) {
              dispatch(refreshFolders());
            }

            dispatch(closeShareModal());
            dispatch(setShareModalIsFetching({ id, isFetchingType: 'idle' }));
            dispatch(notify('Настройки сохранены'));
          });

          break;

        default:
          break;
      }
    } catch (e) {
      showError();
      dispatch(setShareModalIsFetching({ id, isFetchingType: 'idle' }));
      logger.error(e, {
        description: 'Fail to create collection page',
      });
    }
  };
